import { ROUTES } from '@/shared/config'
import { navigateToFx } from '@/shared/lib/providers'
import {
  DislikeOutlined,
  FolderAddOutlined,
  LikeOutlined,
  SearchOutlined,
  ShareAltOutlined,
  VerticalLeftOutlined,
} from '@ant-design/icons'
import { Button, type TableProps, Typography } from 'antd'
import { useUnit } from 'effector-react'

import { type GetStatisticsTracksQueryFinishedSuccessResultTracksItem } from '../../api'
import { getTracksStatisticsSortOrderByField } from '../../lib/types/tracks'
import { $sorterResult } from '../../model/statistics-tracks-data.model'

export const useTracksStatisticsTableColumns =
  (): TableProps<GetStatisticsTracksQueryFinishedSuccessResultTracksItem>['columns'] => {
    const sorterResult = useUnit($sorterResult)

    const columns: TableProps<GetStatisticsTracksQueryFinishedSuccessResultTracksItem>['columns'] =
      [
        {
          title: 'Трек',
          width: 150,
          dataIndex: 'name',
          key: 'name',
          sorter: true,
          sortOrder: getTracksStatisticsSortOrderByField(sorterResult, 'name'),
          render: (_, track) => {
            const route = ROUTES.AUDIO.EDIT.replace(':id', track.id)
            return (
              <span key={track.id}>
                <Typography.Link
                  onClick={() => void navigateToFx({ to: route })}
                >
                  {track.name}
                </Typography.Link>
              </span>
            )
          },
        },
        {
          title: 'Автор',
          width: 150,
          dataIndex: 'author',
          key: 'author',
          render: (_, track) => {
            if (!track.authors) return ''
            const mainAuthors = track.authors.filter((a) => !a.isCoauthor)
            const coAuthors = track.authors.filter((a) => a.isCoauthor)
            const authors = [...mainAuthors, ...coAuthors]
            return (
              <>
                {authors.map((author, i) => {
                  const isLast = i === authors.length - 1
                  const route = ROUTES.AUTHORS.EDIT.replace(':id', author.id)

                  return (
                    <span key={author.id}>
                      <Typography.Link
                        onClick={() => void navigateToFx({ to: route })}
                      >
                        {author.nickname}
                      </Typography.Link>
                      {!isLast && ',\u00A0'}
                    </span>
                  )
                })}
              </>
            )
          },
        },
        {
          title: <LikeOutlined title="Нравится" />,
          width: 100,
          ellipsis: true,
          dataIndex: 'likesCount',
          key: 'likesCount',
          sorter: true,
          sortOrder: getTracksStatisticsSortOrderByField(
            sorterResult,
            'likesCount',
          ),
        },
        {
          title: <DislikeOutlined title="Не нравится" />,
          width: 100,
          ellipsis: true,
          dataIndex: 'dislikesCount',
          key: 'dislikesCount',
          sorter: true,
          sortOrder: getTracksStatisticsSortOrderByField(
            sorterResult,
            'dislikesCount',
          ),
        },
        {
          title: <ShareAltOutlined title="Поделились" />,
          width: 100,
          ellipsis: true,
          dataIndex: 'sharesCount',
          key: 'sharesCount',
          sorter: true,
          sortOrder: getTracksStatisticsSortOrderByField(
            sorterResult,
            'sharesCount',
          ),
        },
        {
          title: <FolderAddOutlined title="Добавлений в плейлист" />,
          width: 100,
          ellipsis: true,
          dataIndex: 'playlistsCount',
          key: 'playlistsCount',
          sorter: true,
          sortOrder: getTracksStatisticsSortOrderByField(
            sorterResult,
            'playlistsCount',
          ),
        },
        {
          title: 'Прослушиваний',
          children: [
            {
              title: <span title="Более 50% трека">{'>50%'}</span>,
              width: 100,
              ellipsis: true,
              dataIndex: 'listensCount50',
              key: 'listensCount50',
              sorter: true,
              sortOrder: getTracksStatisticsSortOrderByField(
                sorterResult,
                'listensCount50',
              ),
            },
            {
              title: <span title="Более 75% трека">{'>75%'}</span>,
              width: 100,
              ellipsis: true,
              dataIndex: 'listensCount75',
              key: 'listensCount75',
              sorter: true,
              sortOrder: getTracksStatisticsSortOrderByField(
                sorterResult,
                'listensCount75',
              ),
            },
            {
              title: <span title="Более 95% трека">{'>95%'}</span>,
              width: 100,
              ellipsis: true,
              dataIndex: 'listensCount95',
              key: 'listensCount95',
              sorter: true,
              sortOrder: getTracksStatisticsSortOrderByField(
                sorterResult,
                'listensCount95',
              ),
            },
            {
              title: 'Всего',
              width: 100,
              ellipsis: true,
              dataIndex: 'listensCount',
              key: 'listensCount',
              sorter: true,
              sortOrder: getTracksStatisticsSortOrderByField(
                sorterResult,
                'listensCount',
              ),
            },
          ],
        },
        {
          title: <SearchOutlined title="Запусков из поиска" />,
          width: 100,
          ellipsis: true,
          dataIndex: 'searchesCount',
          key: 'searchesCount',
          sorter: true,
          sortOrder: getTracksStatisticsSortOrderByField(
            sorterResult,
            'searchesCount',
          ),
        },
        {
          title: <VerticalLeftOutlined title="Пропусков" />,
          width: 100,
          ellipsis: true,
          dataIndex: 'swapsCount',
          key: 'swapsCount',
          sorter: true,
          sortOrder: getTracksStatisticsSortOrderByField(
            sorterResult,
            'swapsCount',
          ),
        },
      ]

    return columns
  }
